import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, Metric, SectionLabel, Pill } from "@/components/common";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { FlaskConical, Play, BookmarkPlus } from "lucide-react";
import { UpgradeGate } from "@/components/app/UpgradeGate";

const STRATS = ["Trend Pullback", "Trend Following"];
const TFS = ["15M", "1H", "4H"];

function BacktestTool() {
  const [p, setP] = useState({ strategy: "Trend Pullback", timeframe: "15M", bars: 600, risk_pct: 0.5, account_size: 10000, target_r: 2.0, spread: 0.30, slippage: 0.10, commission: 0.0, forward_bars: 250 });
  const [res, setRes] = useState(null);
  const [wf, setWf] = useState(null);
  const [mode, setMode] = useState("standard");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true); setRes(null); setWf(null);
    try {
      if (mode === "walkforward") {
        const { data } = await api.post("/backtest/walkforward", p);
        setWf(data.result);
        toast.success("Walk-forward complete.");
      } else {
        const { data } = await api.post("/backtest/run", p);
        setRes(data.result);
        if (data.result.empty) toast.info("No qualifying setups in this period.");
        else toast.success(`Backtest complete: ${data.result.total_trades} trades.`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Backtest failed.");
    }
    finally { setLoading(false); }
  };

  const saveAsStrategy = async () => {
    const regime = p.strategy.toLowerCase().includes("range")
      ? ["RANGE"] : ["UPTREND", "DOWNTREND"];
    const summary = mode === "walkforward" ? wf?.forward : res;
    try {
      await api.post("/strategies", {
        name: `${p.strategy} · ${p.timeframe} (backtested)`, timeframe: p.timeframe,
        source: "backtest", active: true,
        backtest: summary ? { win_rate: summary.win_rate, profit_factor: summary.profit_factor, return_pct: summary.return_pct } : null,
        rules: { regime_filter: regime, direction: "both", min_setup_score: 60, min_rr: p.target_r, session_filter: [], news_filter: true },
      });
      toast.success("Saved as an active monitoring strategy — Aurum is now watching Gold for it.");
    } catch { toast.error("Could not save strategy."); }
  };

  const hasResults = (mode === "standard" && res && !res.empty) || (mode === "walkforward" && wf);

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3"><FlaskConical className="text-aurum" /><h1 className="text-2xl font-bold tracking-tight">Backtesting Lab</h1><Pill color="#F5B942">DEMO / HISTORICAL</Pill></div>
        {hasResults && (
          <button onClick={saveAsStrategy} data-testid="save-as-strategy" className="flex items-center gap-2 rounded-full border border-aurum text-aurum px-4 py-2 text-sm font-semibold hover:bg-[rgba(214,174,87,0.1)] transition-colors">
            <BookmarkPlus size={15} /> Save as monitoring strategy
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-4 gap-5">
        <Card className="p-5 lg:col-span-1 space-y-4">
          <SectionLabel>Configuration</SectionLabel>
          <div className="flex rounded-lg border border-[var(--border2)] p-0.5 text-xs">
            <button onClick={() => setMode("standard")} className={`flex-1 rounded-md px-2 py-1.5 ${mode === "standard" ? "btn-gold font-semibold" : "text-[var(--text-sec)]"}`} data-testid="bt-mode-standard">Standard</button>
            <button onClick={() => setMode("walkforward")} className={`flex-1 rounded-md px-2 py-1.5 ${mode === "walkforward" ? "btn-gold font-semibold" : "text-[var(--text-sec)]"}`} data-testid="bt-mode-walkforward">Walk-Forward</button>
          </div>
          <div>
            <label className="text-xs text-[var(--text-sec)]">Strategy</label>
            <select value={p.strategy} onChange={(e) => setP({ ...p, strategy: e.target.value })} className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum" data-testid="bt-strategy">
              {STRATS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[var(--text-sec)]">Timeframe</label>
            <div className="mt-1 flex gap-1">{TFS.map((t) => <button key={t} onClick={() => setP({ ...p, timeframe: t })} className={`flex-1 rounded-lg border px-2 py-1.5 text-xs ${p.timeframe === t ? "border-aurum text-aurum" : "border-[var(--border2)]"}`}>{t}</button>)}</div>
          </div>
          {[["bars", mode === "walkforward" ? "Historical bars" : "Historical bars"], ...(mode === "walkforward" ? [["forward_bars", "Forward (unseen) bars"]] : []), ["risk_pct", "Risk % / trade"], ["account_size", "Account size ($)"], ["target_r", "Target R multiple"], ["spread", "Spread assumption"], ["slippage", "Slippage assumption"], ["commission", "Commission / trade ($)"]].map(([k, l]) => (
            <div key={k}><label className="text-xs text-[var(--text-sec)]">{l}</label>
              <input type="number" step="0.01" value={p[k]} onChange={(e) => setP({ ...p, [k]: Number(e.target.value) })} className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum tnum" data-testid={`bt-${k}`} /></div>
          ))}
          <button onClick={run} disabled={loading} data-testid="bt-run" className="btn-gold w-full rounded-lg py-2.5 text-sm font-semibold hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2">
            <Play size={15} /> {loading ? "Running…" : mode === "walkforward" ? "Run walk-forward" : "Run backtest"}
          </button>
        </Card>

        <div className="lg:col-span-3 space-y-5">
          {mode === "walkforward" ? (
            !wf ? (
              <Card className="p-10 text-center"><div className="text-sm text-[var(--text-muted)]">Walk-forward runs your strategy on older history, then independently on the most recent <b>unseen</b> bars — reported separately so you can judge robustness.</div></Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-5">
                <SegmentReport title="Historical" sub={`${wf.historical_bars} bars`} r={wf.historical} accent="#60A5FA" />
                <SegmentReport title="Forward Test (unseen)" sub={`most recent ${wf.forward_bars} bars`} r={wf.forward} accent="#21C58E" />
                <p className="md:col-span-2 text-xs text-[var(--text-muted)]">FORWARD TEST runs on the most recent data the strategy did not "see" during the historical window. A strategy that holds up out-of-sample is more trustworthy — but nothing guarantees future results.</p>
              </div>
            )
          ) : !res ? (
            <Card className="p-10 text-center"><div className="text-sm text-[var(--text-muted)]">Configure and run a backtest to see results, equity curve and in/out-of-sample split.</div></Card>
          ) : res.empty ? (
            <Card className="p-10 text-center"><div className="text-sm text-[var(--text-sec)]">{res.message}</div></Card>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4"><Metric label="Trades" value={res.total_trades} testId="bt-trades" /></Card>
                <Card className="p-4"><Metric label="Win Rate" value={`${res.win_rate}%`} /></Card>
                <Card className="p-4"><Metric label="Profit Factor" value={res.profit_factor} /></Card>
                <Card className="p-4"><Metric label="Return" value={`${res.return_pct}%`} color={res.return_pct >= 0 ? "#21C58E" : "#EF6262"} /></Card>
                <Card className="p-4"><Metric label="Avg R" value={res.avg_r} /></Card>
                <Card className="p-4"><Metric label="Expectancy" value={`$${res.expectancy}`} /></Card>
                <Card className="p-4"><Metric label="Max Drawdown" value={`$${res.max_drawdown}`} /></Card>
                <Card className="p-4"><Metric label="Max Consec. Losses" value={res.max_consecutive_losses} /></Card>
              </div>
              <Card className="p-5">
                <SectionLabel>Equity Curve</SectionLabel>
                <div className="mt-3" style={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={res.equity_curve}>
                      <XAxis dataKey="i" stroke="#667085" fontSize={11} /><YAxis stroke="#667085" fontSize={11} domain={["auto", "auto"]} />
                      <Tooltip contentStyle={{ background: "#171E28", border: "1px solid #212B3B", borderRadius: 8, color: "#F5F7FA" }} />
                      <Line type="monotone" dataKey="equity" stroke="#D6AE57" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
              <div className="grid md:grid-cols-2 gap-5">
                <Card className="p-5"><SectionLabel>In-Sample (first 60%)</SectionLabel><SegView s={res.in_sample} /></Card>
                <Card className="p-5"><SectionLabel>Out-of-Sample (last 40%)</SectionLabel><SegView s={res.out_of_sample} /></Card>
              </div>
              <p className="text-xs text-[var(--text-muted)]">Assumptions: spread {p.spread}, slippage {p.slippage}, commission ${p.commission}. No look-ahead bias. Past backtest results do not guarantee future performance.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SegmentReport({ title, sub, r, accent }) {
  return (
    <Card className="p-5" data-testid={`wf-${title.toLowerCase().includes("forward") ? "forward" : "historical"}`}>
      <div className="flex items-center justify-between mb-3">
        <div><SectionLabel>{title}</SectionLabel><div className="text-[11px] text-[var(--text-muted)]">{sub}</div></div>
        <Pill color={accent}>{title.includes("Forward") ? "OUT-OF-SAMPLE" : "IN-SAMPLE"}</Pill>
      </div>
      {r.empty ? <div className="text-sm text-[var(--text-muted)] py-6 text-center">{r.message}</div> : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Metric label="Trades" value={r.total_trades} />
            <Metric label="Win Rate" value={`${r.win_rate}%`} />
            <Metric label="Profit Factor" value={r.profit_factor} />
            <Metric label="Return" value={`${r.return_pct}%`} color={r.return_pct >= 0 ? "#21C58E" : "#EF6262"} />
            <Metric label="Avg R" value={r.avg_r} />
            <Metric label="Max DD" value={`$${r.max_drawdown}`} />
          </div>
          <div className="mt-3" style={{ height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={r.equity_curve}>
                <XAxis dataKey="i" stroke="#667085" fontSize={10} hide /><YAxis stroke="#667085" fontSize={10} domain={["auto", "auto"]} width={44} />
                <Tooltip contentStyle={{ background: "#171E28", border: "1px solid #212B3B", borderRadius: 8, color: "#F5F7FA" }} />
                <Line type="monotone" dataKey="equity" stroke={accent} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </Card>
  );
}

function SegView({ s }) {
  return (
    <div className="mt-3 grid grid-cols-2 gap-3">
      <Metric label="Trades" value={s.trades} />
      <Metric label="Win Rate" value={`${s.win_rate}%`} />
      <Metric label="Avg R" value={s.avg_r} />
      <Metric label="Net P/L" value={`$${s.net}`} color={s.net >= 0 ? "#21C58E" : "#EF6262"} />
    </div>
  );
}

export default function Backtest() {
  return (
    <UpgradeGate feature="backtesting" title="Backtesting Lab"
      blurb="Validate your edge on historical Gold data — with realistic spread, slippage and commission — before you risk real capital. Walk-forward testing on Elite."
      requiredPlan="Pro">
      <BacktestTool />
    </UpgradeGate>
  );
}
